'use client';

/**
 * The classic single-pane chat area (tab strip off): loading, the active chat,
 * or the start screen — exactly one of the three.
 *
 * The loading state is gated on a chat being SELECTED as well as the query
 * being in flight, so the start screen stays interactive while chat history is
 * still being fetched in the background.
 */

import React from 'react';
import { ChatLoadingState, ChatStartScreen, ChatActiveChatState } from '../ui/index.js';
import type { FilePart } from '../../lib/db/types.js';
import type { AttachedSkill } from '../skills/types.js';
import type { StartChatOptions } from '../ui/start-screen/useStartScreenComposer.js';
import type { BusinessInfo } from '../../types/index.js';

export interface ChatSinglePaneAreaProps {
  selectedChatId: string | null;
  selectedChat: React.ComponentProps<typeof ChatActiveChatState>['selectedChat'] | null;
  isMessagesLoading: boolean;
  apiBaseUrl: string;
  locale: string;
  isLoading: boolean;
  initialQuery: string;
  initialFiles: FilePart[];
  initialSkill: AttachedSkill | null;
  userContext: BusinessInfo | null;
  messages: React.ComponentProps<typeof ChatActiveChatState>['messages'];
  hasMoreMessages: boolean;
  isLoadingOlderMessages: boolean;
  onMessageSent: () => void;
  onLoadOlderMessages: () => void;
  onShare: () => void;
  onArchive: React.ComponentProps<typeof ChatActiveChatState>['onArchive'];
  onDelete: React.ComponentProps<typeof ChatActiveChatState>['onDelete'];
  onStartChat: (query: string, options: StartChatOptions) => void;
  onBackToList: () => void;
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  meridianLogoUrl: string;
  mcpConnectionsHref?: string;
  compactStartScreen: boolean;
  showSlackCallout: boolean;
  slackCalloutMentionsSearch: boolean;
  showModelPicker: boolean;
}

export function ChatSinglePaneArea({
  selectedChatId,
  selectedChat,
  isMessagesLoading,
  apiBaseUrl,
  locale,
  isLoading,
  initialQuery,
  initialFiles,
  initialSkill,
  userContext,
  messages,
  hasMoreMessages,
  isLoadingOlderMessages,
  onMessageSent,
  onLoadOlderMessages,
  onShare,
  onArchive,
  onDelete,
  onStartChat,
  onBackToList,
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  meridianLogoUrl,
  mcpConnectionsHref,
  compactStartScreen,
  showSlackCallout,
  slackCalloutMentionsSearch,
  showModelPicker,
}: ChatSinglePaneAreaProps) {
  if (selectedChatId && isMessagesLoading) {
    return <ChatLoadingState meridianLogoUrl={meridianLogoUrl} onBackToList={onBackToList} />;
  }

  if (selectedChat) {
    return (
      <ChatActiveChatState
        selectedChatId={selectedChatId!}
        selectedChat={selectedChat}
        apiBaseUrl={apiBaseUrl}
        locale={locale}
        initialQuery={initialQuery}
        initialFiles={initialFiles}
        initialSkill={initialSkill}
        hasMoreMessages={hasMoreMessages}
        isLoadingOlderMessages={isLoadingOlderMessages}
        messages={messages}
        onMessageSent={onMessageSent}
        onLoadOlderMessages={onLoadOlderMessages}
        onShare={onShare}
        onArchive={onArchive}
        onDelete={onDelete}
        botAvatarUrl={botAvatarUrl}
        userInitials={userInitials}
        userAvatarUrl={userAvatarUrl}
        meridianLogoUrl={meridianLogoUrl}
        mcpConnectionsHref={mcpConnectionsHref}
      />
    );
  }

  return (
    <ChatStartScreen
      userContext={userContext}
      locale={locale}
      // Keep input interactive even while chat history is still fetching. This
      // loading flag is specifically for "starting a new chat" UX.
      isLoading={isLoading}
      onStartChat={onStartChat}
      compact={compactStartScreen}
      meridianLogoUrl={meridianLogoUrl}
      showSlackCallout={showSlackCallout}
      slackCalloutMentionsSearch={slackCalloutMentionsSearch}
      mcpConnectionsHref={mcpConnectionsHref}
      apiBaseUrl={apiBaseUrl}
      // Hosts without Meridian API access (extension pre-auth) can't load the
      // model catalog — hide the picker rather than show a dead chip.
      showModelPicker={showModelPicker}
    />
  );
}
